import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube } from '@fortawesome/free-solid-svg-icons';
import './Map3DToggle.scss';
import { setMap3D } from './map3DMode';

/** Fired on the map whenever the user switches between 2D and 3D. Payload: `{ is3D: boolean }`. */
export const MODE_3D_EVENT = 'em3:mode3d';

interface Map3DToggleProps {
  map: mapboxgl.Map;
}

const Map3DToggle: React.FC<Map3DToggleProps> = ({ map }) => {
  const [is3D, setIs3D] = useState(false);

  const handleToggle = useCallback(() => {
    // The mode is recorded BEFORE it is announced, so a listener that asks the
    // mode while handling the event reads the new one (#820 step 8). The button
    // follows the recorded mode before the announcement too, so a listener that
    // throws cannot leave the button saying 2D over a 3D map (#866 M6).
    if (is3D) {
      map.dragRotate.disable();
      map.touchPitch.disable();
      map.resetNorthPitch();
      setMap3D(map, false);
      setIs3D(false);
      map.fire(MODE_3D_EVENT, { is3D: false });
    } else {
      map.dragRotate.enable();
      map.touchPitch.enable();
      setMap3D(map, true);
      setIs3D(true);
      map.fire(MODE_3D_EVENT, { is3D: true });
    }
  }, [is3D, map]);

  return (
    <button
      className={`map-3d-toggle${is3D ? ' active' : ''}`}
      onClick={handleToggle}
      aria-label={is3D ? 'Disable 3D rotation' : 'Enable 3D rotation'}
      title={is3D ? 'Disable 3D rotation' : 'Enable 3D rotation'}
    >
      <FontAwesomeIcon icon={faCube} />
    </button>
  );
};

export default Map3DToggle;
